import React, { createContext, useContext, useEffect, useState } from 'react';
import { setSiteNameForTitle } from '@/components/shared/PageTitle/PageTitleSync';
import { TenantApi } from '@/services/tenant.service';
import { TenantBranding, type UiTheme } from '@/types/tenant.types';
import { useAuth } from '@/contexts/shared/AuthContext';
import { applyTenantAccent } from '@/utils/tenantAccent';

interface BrandingContextType {
  branding: TenantBranding | null;
  loading: boolean;
  logoUrl: string | null;
  logoLightUrl: string | null;
  /** Square logo marks — shown when the sidebar menu is collapsed */
  logoMarkUrl: string | null;
  logoMarkLightUrl: string | null;
  faviconUrl: string | null;
  loginPageText: string | null;
  footerText: string | null;
  /** True when the tenant has allow_specific_departments enabled */
  allowDepartments: boolean;
  /** True when the tenant has allow_specific_positions enabled */
  allowPositions: boolean;
  /** Admin-selected interface theme (data-ui on <html>). */
  uiTheme: UiTheme;
  /** False only on a browser's very first signed-in load, while the tenant's
   *  theme is still on its way: the shell waits instead of drawing the default
   *  theme and then rebuilding every page in the other one. */
  uiThemeKnown: boolean;
  refreshBranding: () => void;
}

const BrandingContext = createContext<BrandingContextType>({
  branding: null,
  loading: false,
  logoUrl: null,
  logoLightUrl: null,
  logoMarkUrl: null,
  logoMarkLightUrl: null,
  faviconUrl: null,
  loginPageText: null,
  footerText: null,
  allowDepartments: false,
  allowPositions: false,
  uiTheme: 'bento',
  uiThemeKnown: true,
  refreshBranding: () => {},
});

export const useBranding = () => useContext(BrandingContext);

const getFileUrl = (fileId: number | undefined | null) => {
  if (!fileId) return null;
  return `${import.meta.env.VITE_API_URL || ''}/v1/files/${fileId}/content`;
};

// ── Tab icon + title ────────────────────────────────────────────────────────
// Both are remembered for index.html, which applies them before the first paint.
const FAVICON_LS_KEY = 'ilearn_favicon';
const SITE_NAME_LS_KEY = 'ilearn_site_name';
const DEFAULT_SITE_NAME = 'iLearn';
// The default set (also in index.html): the iLearn mark as SVG for Chromium/Firefox, PNG for Safari, which
// ignores SVG favicons, and a touch icon. A tenant's own favicon replaces the whole set.
const DEFAULT_ICONS: Array<Record<string, string>> = [
  { rel: 'icon', type: 'image/svg+xml', href: '/favicon.svg' },
  { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32.png' },
  { rel: 'icon', type: 'image/png', sizes: '192x192', href: '/favicon-192.png' },
  { rel: 'apple-touch-icon', href: '/apple-touch-icon.png' },
];

const setFavicon = (url: string | null) => {
  const wanted = url ? [{ rel: 'icon', href: url }, { rel: 'apple-touch-icon', href: url }] : DEFAULT_ICONS;
  const links = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel*="icon"]'));
  const same = links.length === wanted.length
    && wanted.every((w, i) => links[i].getAttribute('rel') === w.rel && links[i].getAttribute('href') === w.href);
  if (!same) {
    links.forEach(el => el.remove());
    for (const attrs of wanted) {
      const link = document.createElement('link');
      Object.entries(attrs).forEach(([k, v]) => link.setAttribute(k, v));
      document.head.appendChild(link);
    }
  }
  try {
    if (url) localStorage.setItem(FAVICON_LS_KEY, url);
    else localStorage.removeItem(FAVICON_LS_KEY);
  } catch { /* storage unavailable */ }
};

/** The tab title is the tenant's name (the site name), "iLearn" when it has none. */
const setSiteName = (name: string | null | undefined) => {
  const clean = (name || '').trim();
  // The tab reads "<page> · <site name>" (PageTitleSync); this sets the site part.
  setSiteNameForTitle(clean || DEFAULT_SITE_NAME);
  try {
    if (clean) localStorage.setItem(SITE_NAME_LS_KEY, clean);
    else localStorage.removeItem(SITE_NAME_LS_KEY);
  } catch { /* storage unavailable */ }
};

// ── Custom CSS injection ────────────────────────────────────────────────────
const CUSTOM_CSS_ID = 'tenant-custom-css';
const injectCustomCSS = (css: string | undefined | null) => {
  let el = document.getElementById(CUSTOM_CSS_ID) as HTMLStyleElement | null;
  if (!css) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('style');
    el.id = CUSTOM_CSS_ID;
    document.head.appendChild(el);
  }
  el.textContent = css;
};

// ── Custom font injection ────────────────────────────────────────────────────
const CUSTOM_FONT_ID = 'tenant-custom-font';
const injectCustomFont = (fontValue: string | undefined | null) => {
  let el = document.getElementById(CUSTOM_FONT_ID) as HTMLStyleElement | null;
  if (!fontValue) {
    if (el) el.remove();
    document.documentElement.style.removeProperty('--tenant-font');
    document.body.style.removeProperty('font-family');
    return;
  }
  // Value is stored as "dataURL||filename" — extract the data URL part
  const dataUrl = fontValue.includes('||') ? fontValue.split('||')[0] : fontValue;
  // Only inject if it's an actual data URL (not just a filename leftover from old saves)
  if (!dataUrl.startsWith('data:')) return;

  if (!el) {
    el = document.createElement('style');
    el.id = CUSTOM_FONT_ID;
    document.head.appendChild(el);
  }
  el.textContent = `
    @font-face {
      font-family: 'TenantCustomFont';
      src: url('${dataUrl}') format('truetype');
      font-weight: normal;
      font-style: normal;
    }
    :root { --tenant-font: 'TenantCustomFont', sans-serif; }
    body, button, input, select, textarea { font-family: var(--tenant-font) !important; }
  `;
};

// ── Tenant CSS attribute helpers ────────────────────────────────────────────
const applyTenantAttributes = (data: TenantBranding | null) => {
  const html = document.documentElement;
  if (!data) {
    html.removeAttribute('data-tenant');
    html.removeAttribute('data-tenant-number');
    html.removeAttribute('data-tenant-name');
    return;
  }
  if (data._tenant_number) html.setAttribute('data-tenant', data._tenant_number);
  else if (data._tenant_id)  html.setAttribute('data-tenant', data._tenant_id);
  if (data._tenant_number)   html.setAttribute('data-tenant-number', data._tenant_number);
  if (data._tenant_name)     html.setAttribute('data-tenant-name', data._tenant_name);
};

// ── Design preset (data-brand on <html>, cached for the pre-paint script) ───
const BRAND_LS_KEY = 'ilearn_brand';
const applyDesignPreset = (data: TenantBranding | null) => {
  const html = document.documentElement;
  const preset = data?.ui_preset && data.ui_preset !== 'ilearn' ? data.ui_preset : null;
  try {
    if (preset) localStorage.setItem(BRAND_LS_KEY, preset);
    else localStorage.removeItem(BRAND_LS_KEY);
  } catch { /* storage blocked — attribute alone still works */ }
  if (preset) html.setAttribute('data-brand', preset);
  else html.removeAttribute('data-brand');
};

// ── Interface theme (data-ui on <html>, cached for the pre-paint script) ────
const UI_LS_KEY = 'ilearn_ui';
export const readCachedUiTheme = (): UiTheme => {
  try { return localStorage.getItem(UI_LS_KEY) === 'classic' ? 'classic' : 'bento'; } catch { return 'bento'; }
};
/** Whether this browser has seen the tenant's theme before (both values are kept). */
const hasCachedUiTheme = (): boolean => {
  try { return localStorage.getItem(UI_LS_KEY) != null; } catch { return true; }
};
const applyUiTheme = (theme: UiTheme) => {
  try {
    localStorage.setItem(UI_LS_KEY, theme);
  } catch { /* storage blocked — attribute alone still works */ }
  document.documentElement.setAttribute('data-ui', theme);
};

// ── CSS variable mapping ────────────────────────────────────────────────────
const applyBrandingStyles = (data: TenantBranding | null) => {
  applyDesignPreset(data);
  const root = document.documentElement;

  const set = (varName: string, value: string | undefined | null) => {
    if (value) root.style.setProperty(varName, value);
    else root.style.removeProperty(varName);
  };

  if (!data) {
    // Reset all custom vars
    [
      '--purple-1', '--purple-dark-1', '--dm-purple-dark-1',
      '--dm-purple-1-on-hover', '--link-color',
      '--button-bg-color', '--button-text-color', '--button-hover-bg-color',
    ].forEach(v => root.style.removeProperty(v));
    applyTenantAccent(null);
    injectCustomCSS(null);
    injectCustomFont(null);
    applyTenantAttributes(null);
    return;
  }

  // ── Primary brand color ──────────────────────────────────────────────
  // SAFE MAPPING ONLY:
  //   --purple-dark-1 / --dm-purple-dark-1 → button outlined bg, active row bg
  //   --purple-1                           → filled button background
  //
  // DO NOT touch --dm-purple-1 here.
  // --dm-purple-1 is used as a TEXT COLOR (badges, links, active tab text,
  // icon fills via `fill: currentColor`). Setting it to a dark primary_color
  // makes ALL icons and accent text invisible in dark mode.
  const primary = data.primary_color;
  set('--purple-dark-1',    primary);
  set('--dm-purple-dark-1', primary);
  set('--purple-1',         primary);   // filled button bg

  // Hover
  const hoverColor = data.button_hover_bg_color || null;
  set('--dm-purple-1-on-hover', hoverColor || primary);
  set('--button-hover-bg-color', hoverColor);

  // Secondary brand color (accent light)
  set('--purple-light-1', data.secondary_color);

  // Link color
  set('--link-color', data.link_color);

  // ── Fine-grained button overrides ────────────────────────────────────
  // button_bg_color is an explicit override for the filled button color.
  // It is safe to also update --dm-purple-1 here because the admin
  // deliberately chose this color for UI accents.
  if (data.button_bg_color) {
    set('--button-bg-color',  data.button_bg_color);
    set('--purple-1',         data.button_bg_color);
    set('--purple-dark-1',    data.button_bg_color);
    set('--dm-purple-dark-1', data.button_bg_color);
  }

  if (data.button_text_color) {
    set('--button-text-color', data.button_text_color);
    // Button.module.css reads --button-text-color with var(--white) fallback.
    // Do NOT override --white / --dm-white globally — that breaks all text.
  }

  if (data.button_hover_bg_color) {
    set('--dm-purple-1-on-hover', data.button_hover_bg_color);
  }

  // Accent colour for the redesigned UI (UAT DEF-180): drives the --accent
  // tokens; the legacy --purple-* vars above follow them through the bridge.
  applyTenantAccent(data.accent_color);

  // Custom CSS (injected into <head> as a <style> tag)
  injectCustomCSS(data.custom_css);

  // Custom font (injected as a @font-face rule applied to the whole page)
  injectCustomFont(data.custom_font_url);

  // Tenant identity attributes on <html> for CSS selector targeting
  applyTenantAttributes(data);
};

// ── Provider ────────────────────────────────────────────────────────────────
export const BrandingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [branding, setBranding] = useState<TenantBranding | null>(null);
  const [loading, setLoading] = useState(true);
  // Signed out: the tenant's interface theme from its public branding (the public pages, the visitor's course page).
  const [guestUiTheme, setGuestUiTheme] = useState<UiTheme | null>(null);
  const { user, isLoading: authLoading } = useAuth();
  // Whose branding fetch has finished ('guest' signed out, else the user id). Until the current visitor's
  // has, the interface theme isn't known — on the sign-in page the default 'bento' used to be remembered
  // before the public branding (with the tenant's theme) arrived, so the first dashboard after signing in
  // was drawn — and its widgets fetched — in the wrong theme, then again in the right one.
  const [settledFor, setSettledFor] = useState<string | null>(null);
  const visitor = user ? String(user.id) : 'guest';

  const fetchBranding = async () => {
    if (!user) {
      setBranding(null);
      applyBrandingStyles(null);
      setLoading(false);
      // The tenant's tab icon and name apply signed out too (login, guest page,
      // legal pages); branding is public, so only those are taken here.
      try {
        const data = await TenantApi.getCurrentBranding();
        setFavicon(data.favicon_url || null);
        setSiteName(data._tenant_name);
        if (data.ui_theme) setGuestUiTheme(data.ui_theme === 'classic' ? 'classic' : 'bento');
      } catch {
        // Backend unreachable: keep the icon and name of the last visit (index.html
        // already applied them) instead of swapping in the defaults.
      } finally {
        setSettledFor('guest');
      }
      return;
    }

    try {
      setLoading(true);
      const data = await TenantApi.getCurrentBranding();
      setBranding(data);
      applyBrandingStyles(data);
      setFavicon(data.favicon_url || null);
      setSiteName(data._tenant_name);
    } catch (error) {
      console.error('Failed to load branding', error);
      setBranding(null);
      applyBrandingStyles(null);
      // Icon and name stay as they were (see above).
    } finally {
      setLoading(false);
      setSettledFor(String(user.id));
    }
  };

  useEffect(() => {
    // Wait until the session is known: the signed-out call resolves the tenant from the stored id and
    // would briefly give a user whose own tenant is about to load another tenant's icon, or the default.
    if (authLoading) return;
    fetchBranding();
  }, [user, authLoading]);

  const logoUrl      = branding?.logo_url       || getFileUrl(branding?.logo_file_id);
  const logoLightUrl = branding?.logo_light_url  || null;
  const logoMarkUrl      = branding?.logo_mark_url       || null;
  const logoMarkLightUrl = branding?.logo_mark_light_url || null;
  const faviconUrl   = branding?.favicon_url     || null;
  const loginPageText = branding?.login_page_text || null;
  const footerText   = branding?.footer_text     || null;
  const allowDepartments = branding?.allow_specific_departments ?? false;
  const allowPositions   = branding?.allow_specific_positions   ?? false;
  // Until branding loads, keep the cached choice so the shell doesn't flip.
  const uiTheme: UiTheme = branding
    ? (branding.ui_theme === 'classic' ? 'classic' : 'bento')
    : (!user && guestUiTheme) || readCachedUiTheme();
  const uiThemeKnown = !!branding || settledFor === visitor || hasCachedUiTheme();
  // Remember it only once it is the tenant's own choice (or the fetch is over).
  useEffect(() => { if (uiThemeKnown) applyUiTheme(uiTheme); }, [uiTheme, uiThemeKnown]);

  return (
    <BrandingContext.Provider value={{
      branding,
      loading,
      logoUrl,
      logoLightUrl,
      logoMarkUrl,
      logoMarkLightUrl,
      faviconUrl,
      loginPageText,
      footerText,
      allowDepartments,
      allowPositions,
      uiTheme,
      uiThemeKnown,
      refreshBranding: fetchBranding,
    }}>
      {children}
    </BrandingContext.Provider>
  );
};